import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { PhoneCall, X, Phone, Smartphone, Zap } from 'lucide-react';
import { trackConversion } from '../lib/track';

const WA_NUMBER = '5516991378312';
export const SAC_PHONE_DISPLAY = '(16) 3237-1003';
export const SAC_PHONE_TEL = 'tel:+551632371003';
export const CEL_PHONE_DISPLAY = '(16) 99137-8312';
export const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Olá! Vim pelo site e gostaria de mais informações.')}`;

const INTERESSES = [
  'Suporte de TI / Help desk',
  'Assistência técnica (PC / Notebook)',
  'Loja Daten (notebooks, desktops, mini PCs)',
  'Navegamer (PC gamer / periféricos)',
  'Câmeras e alarmes Intelbras',
  'SHOficina ERP',
  'Bling ERP',
  'Sites / Hospedagem',
  'Outro',
];

export const maskPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const inputCls = 'w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/30 outline-none text-white text-base sm:text-sm placeholder:text-slate-500 transition-colors';

const CallbackModal = ({ open, onClose, defaultInteresse = '' }) => {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [interesse, setInteresse] = useState(defaultInteresse);
  const [erro, setErro] = useState('');

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const n = nome.trim();
    const digits = telefone.replace(/\D/g, '');
    if (digits.length < 10) { setErro('Informe um telefone válido com DDD.'); return; }
    setErro('');
    const texto = `Olá! Me chamo ${n}, meu telefone é ${telefone} e quero que a Fernando Tech entre em contato comigo sobre: ${interesse}.\n\n(Enviado pelo botão "Solicitar contato" do site)`;
    try { sessionStorage.setItem('ft_conv_whatsapp', '1'); } catch { /* evita contar 2x */ }
    trackConversion('callback');
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center sm:p-4" data-testid="callback-modal" role="dialog" aria-modal="true" aria-labelledby="callback-title">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full sm:max-w-md bg-slate-900 border border-cyan-500/40 rounded-t-3xl sm:rounded-2xl shadow-2xl shadow-cyan-500/20 p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto"
      >
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800" aria-label="Fechar" data-testid="callback-close">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3 mb-5 pr-8">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30 shrink-0">
            <PhoneCall className="w-6 h-6 text-white" />
          </span>
          <div>
            <h3 id="callback-title" className="text-white font-extrabold text-xl leading-tight">A Fernando Tech liga para você</h3>
            <p className="text-slate-300 text-sm mt-1 leading-snug">
              Informe seu telefone e <strong className="text-cyan-300">nós ligamos de volta</strong> pelo {SAC_PHONE_DISPLAY} ou pelo nosso WhatsApp. Você não precisa ligar.
            </p>
          </div>
        </div>

        <label htmlFor="cb-nome" className="block text-slate-300 text-xs font-semibold mb-1">Seu nome *</label>
        <input
          id="cb-nome"
          value={nome}
          onChange={e => setNome(e.target.value)}
          required
          maxLength={80}
          autoComplete="name"
          placeholder="Como podemos te chamar?"
          className={`${inputCls} mb-3`}
          data-testid="callback-nome"
        />

        <label htmlFor="cb-telefone" className="block text-slate-300 text-xs font-semibold mb-1">Seu telefone / celular com DDD *</label>
        <div className="relative mb-3">
          <Smartphone className="w-4 h-4 text-cyan-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            id="cb-telefone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={telefone}
            onChange={e => setTelefone(maskPhone(e.target.value))}
            required
            minLength={14}
            placeholder="(16) 99999-9999"
            className={`${inputCls} pl-11`}
            data-testid="callback-telefone"
          />
        </div>

        <label htmlFor="cb-interesse" className="block text-slate-300 text-xs font-semibold mb-1">Produto ou serviço de interesse *</label>
        <select
          id="cb-interesse"
          value={interesse}
          onChange={e => setInteresse(e.target.value)}
          required
          className={`${inputCls} mb-4`}
          data-testid="callback-interesse"
        >
          <option value="">Selecione…</option>
          {INTERESSES.map(i => <option key={i} value={i}>{i}</option>)}
        </select>

        {erro && <p className="text-rose-400 text-xs mb-3" data-testid="callback-erro">{erro}</p>}

        <button
          type="submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-extrabold text-base uppercase tracking-wide shadow-xl shadow-cyan-500/40 transition-all hover:scale-[1.02] active:scale-[0.99]"
          data-testid="callback-submit"
        >
          <Zap className="w-5 h-5" /> Quero que me liguem
        </button>

        <p className="mt-3 text-center text-[11px] text-slate-500 leading-snug">
          Seus dados vão para o WhatsApp da Fernando Tech e nossa equipe retorna para o número informado por telefone{' '}
          <a href={SAC_PHONE_TEL} className="text-cyan-400 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3" />{SAC_PHONE_DISPLAY}</a> ou WhatsApp.
        </p>
      </form>
    </div>,
    document.body
  );
};

export const CallbackButton = ({ className = '', label = 'Solicitar contato agora', interesse = '', testId = 'callback-open', compact = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={compact
          ? `inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-xs shadow-lg shadow-cyan-500/30 transition-all hover:scale-105 active:scale-95 ${className}`
          : `inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-extrabold text-sm shadow-lg shadow-cyan-500/30 transition-all hover:scale-105 active:scale-95 ${className}`}
        data-testid={testId}
      >
        <PhoneCall className="w-4 h-4" /> {label}
      </button>
      <CallbackModal open={open} onClose={() => setOpen(false)} defaultInteresse={interesse} />
    </>
  );
};

export default CallbackModal;
